/**
 * Boot slice — the explicit state machine of docs/ARCHITECTURE.md §5.2, plus
 * the check-on-view listeners of §5.4 and the realtime reconciliation of §2.
 *
 * ```
 * 0  paint the shell
 * 1  repo.currentSession()
 * 2  repo.fetchProfile(uid) -> must_reset ? screen 'reset'
 * 3  Promise.all([ snapshot, boards, catalog, steamLink ]) -> hydrate
 * 4  subscribeCrew + subscribePersonal
 * 5  revalidateVisible(): SWR pass over the surface actually on screen
 * 6  install focus / visibilitychange / 5 s ticker
 * ```
 *
 * Both repositories drive step 4 through the same handler bag. `LocalRepository`
 * supplies only `onChange` (a `BroadcastChannel` message cannot say *what*
 * changed), so it takes the refetch path. `SupabaseRepository` supplies
 * `onPatch` / `onEvent` / `onStatus`, so it takes §2.2's patch path — with a
 * full snapshot refetch on every `SUBSCRIBED`, which is where a reconnect lands
 * and is therefore not optional (§2.2 condition 1).
 *
 * Step 4 is not once-and-for-all. `onStatus('error')` means the channel CLOSED,
 * and phoenix drops a closed channel off the socket rather than retrying it, so
 * `resubscribe` rebuilds the pair on a backoff — `subscribeAll` is the only
 * place either channel is created, precisely so there is one of it to re-run.
 * `repo/SupabaseRepository.ts#subscribeCrew` carries the full account.
 *
 * DEVIATION (P2-07): §5.2 stops at step 1 when there is no session. `boot()`
 * here still *attempts* the catalog and the crew snapshot, and lets the
 * repository decide what is readable pre-auth. LocalRepository is dev parity
 * and returns the seed, so `npm run dev:local` shows a populated debug readout
 * on the login screen; SupabaseRepository returns an empty snapshot under RLS
 * and the app stays on `login`. The *screen* semantics of §5.2 are unchanged.
 */

import type { StateCreator } from 'zustand';

import { isCall, LVL } from '../lib/logic.ts';
import type {
  CrewEvent,
  ListEntry,
  Playtest,
  ScoreRec,
  Unsub,
  User,
} from '../lib/types.ts';
import type { CrewPatch, Repository } from '../repo/Repository.ts';
import { patchEchoKey } from '../repo/Repository.ts';
import { clearInFlight, lookupInFlight, whenIdle } from './mutate.ts';
import { browseMode } from './slices/browse.ts';
import { feedMode } from './slices/feed.ts';
import { GI_MAX_BATCH } from './slices/steamFacts.ts';
import { parseEntryUrl, scrubFragment } from '../lib/joinLink.ts';
import { rememberedAsk } from './slices/joinLinks.ts';
import type { StorageLike } from '../repo/LocalRepository.ts';
import { applyResume, browserResumeStorage, readResume } from './resume.ts';
import type { QdStore } from './types.ts';

/** Prototype `this._agoT` — 5 s (line 1298). */
export const AGO_TICK_MS = 5000;
/** §2.2 condition 3: a tab that comes back after this long re-syncs from scratch. */
export const STALE_SOCKET_MS = 60_000;
/** §2.4: coalesce window per actor. */
export const TOAST_COALESCE_MS = 1500;
/**
 * `focus` and `visibilitychange` both fire on one tab switch, so the naive
 * handler ran the visible-screenful revalidation twice per switch. This is the
 * floor between two focus-driven passes; boot and CHECK NOW are not throttled.
 *
 * It is the OUTER of two floors now. `slices/steamFacts.ts#SURFACE_MIN_MS` is
 * the per-appid one, and it deliberately carries the same number: this one stops
 * the pass, that one stops any single appid being re-asked by a different caller
 * (a LOAD MORE, a filter toggled off and on) inside the same window.
 */
export const REVALIDATE_MIN_MS = 15_000;
/** §2.4: "Cap 3 concurrent toasts". */
export const TOAST_MAX_CONCURRENT = 3;
/** §2.4: ≥3 events from one actor collapse to `CHELSEA MADE 4 CALLS`. */
export const TOAST_COLLAPSE_AT = 3;
/**
 * First wait before rebuilding a channel that reported CLOSED, then doubling to
 * `RESUBSCRIBE_MAX_MS`. Short at the start because the common cause is a
 * transient server-side close and the member is looking at a frozen ◆ badge;
 * capped because the other cause is permanent and retrying it fast is just
 * quota spent on the same answer.
 */
export const RESUBSCRIBE_MIN_MS = 2000;
export const RESUBSCRIBE_MAX_MS = 30_000;

export interface BootSlice {
  repo: Repository;
  crewId: string;
  /**
   * Where `store/resume.ts` keeps the screen a reload comes back to, resolved
   * once at construction. Held in the store rather than closed over by `boot`
   * because the RE-AUTHENTICATION paths need it too — `doLogin` and `doReset`
   * live in the session slice, which has no deps of its own, and they are the
   * paths a long-backgrounded tab actually comes home through (RS-09).
   */
  resumeStore: StorageLike | null;

  /** §2: 'idle' before the first subscribe, then the socket's own state. */
  realtimeState: 'idle' | 'subscribed' | 'reconnecting' | 'error';
  /** §2.2 condition 3: when the socket last delivered anything. */
  lastEventAt: number;

  boot: () => Promise<void>;
  hydrateAfterAuth: () => Promise<void>;
  /** §2.2: rebuild both realtime channels. The recovery for a CLOSED channel. */
  resubscribe: () => void;
  refreshSnapshot: () => Promise<void>;
  refreshBoards: () => Promise<void>;
  /**
   * MY GAMES' two tables (3060): the backlog and the party's scores, refetched
   * whole. Small tables, coarse signal — see `SupabaseRepository` bindings.
   */
  refreshMine: () => Promise<void>;
  applyCrewPatch: (p: CrewPatch) => void;
  handleCrewEvent: (e: CrewEvent) => void;
  revalidateVisible: () => Promise<void>;
  installCheckOnView: () => Unsub;
  teardown: () => void;
}

export interface BootDeps {
  repo: Repository;
  crewId: string;
  /**
   * Where `store/resume.ts` keeps the screen a reload should come back to.
   * `undefined` = the browser's `localStorage` (or nothing, in node);
   * `null` = resume is off. Tests inject a `memStorage()`.
   */
  resume?: StorageLike | null | undefined;
}

export const createBootSlice =
  (deps: BootDeps): StateCreator<QdStore, [], [], BootSlice> =>
  (set, get) => {
    // Per-store handles, so two stores in one process (two "tabs" in a test)
    // cannot tear down each other's subscriptions.
    let unsubCrew: Unsub | null = null;
    let unsubPersonal: Unsub | null = null;
    let agoT: ReturnType<typeof setInterval> | null = null;
    let removeListeners: Unsub | null = null;
    let lastFocusRevalAt = 0;
    /**
     * Floor for the §2.2 condition-3 resync, separate from `lastFocusRevalAt`
     * because the two guard different costs. It exists at all because the probe
     * has no self-limiting condition: `refreshSnapshot` is a REST fetch and says
     * nothing about the socket, so it never advances `lastEventAt` — once the
     * probe starts firing it would fire on every focus until real socket traffic
     * arrives, and `focus` + `visibilitychange` both fire on a single tab switch.
     */
    let lastResyncAt = 0;

    // §2.2: a slow snapshot must not land on top of newer realtime patches.
    let snapshotEpoch = 0;
    let snapshotInFlight = 0;
    let pendingPatches: CrewPatch[] = [];

    /**
     * The boards' equivalent, and a stricter one: a boolean, not a counter,
     * because the personal channel's fan-out is per ROW and there is nothing
     * a second concurrent `fetchMyBoards` can learn that the first will not.
     * `refreshBoards` carries the full account.
     */
    let boardsInFlight = false;
    let boardsAgain = false;
    let mineInFlight = false;
    let mineAgain = false;
    // The in-flight run, so a coalesced caller can AWAIT the trailing pass it
    // asked for rather than resolve before its own read has happened — the
    // sign-in hydrate awaits this for MY GAMES' first paint, and a broadcast
    // from the previous account's last write can still be running then.
    let mineRun: Promise<void> = Promise.resolve();

    // §2.4 toast coalescing, per actor.
    const toastWindow = new Map<string, { n: number; timer: ReturnType<typeof setTimeout> }>();
    let queuedToasts: string[] = [];

    // -- the CLOSED recovery (§2.2) ------------------------------------------
    // `repo/SupabaseRepository.ts#subscribeCrew` has the full account of why
    // `error` is the one status we have to act on: phoenix drops a closed
    // channel off the socket, so nothing in supabase-js will ever bring it back.
    // `reconnecting` is deliberately NOT handled here — that is the state
    // phoenix retries out of on its own, and a second retry loop on top of its
    // rejoin timer would tear down channels that were about to heal.
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = 0;
    /** Set by `teardown`, cleared by any real subscribe. Stops a timer outliving the store. */
    let retriesOff = false;

    const clearRetry = (): void => {
      if (retryTimer !== null) clearTimeout(retryTimer);
      retryTimer = null;
    };

    const scheduleResubscribe = (): void => {
      if (retriesOff || retryTimer !== null) return;
      // Doubling from 2 s to 30 s. A channel that closes because the server
      // said so will close again on the next attempt, and hammering a realtime
      // quota to rediscover that helps nobody.
      retryDelay = retryDelay
        ? Math.min(retryDelay * 2, RESUBSCRIBE_MAX_MS)
        : RESUBSCRIBE_MIN_MS;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        get().resubscribe();
      }, retryDelay);
    };

    /** Both channels, torn down and rebuilt. The only place either is created. */
    /**
     * SCHEDULE (3071) is fetched only once the tab has been shown, so a nudge
     * before then has nothing to refresh: the month asks for itself on the
     * way in. A night opened on its own (from a round's BACK) is refreshed too.
     */
    const refreshSchedShown = async (): Promise<void> => {
      const s = get();
      if (s.schSpan) await s.refreshSched();
      else if (s.schDay) await s.schLoadNight(s.schDay);
    };

    const subscribeAll = (uid: string): void => {
      retriesOff = false;
      clearRetry();
      unsubCrew?.();
      unsubPersonal?.();
      unsubCrew = get().repo.subscribeCrew(get().crewId, {
        // Coarse signal: LocalRepository's BroadcastChannel, and the Supabase
        // fallback for a row it could not translate.
        onChange: (ev) => {
          set({ lastEventAt: Date.now() });
          // A crewmate's score (3060) is not in the snapshot either; it has its
          // own small read, and this coarse signal is how it arrives.
          if (ev.table === 'game_scores' || ev.table === 'recommendations' || ev.table === 'board_events') {
            void get().refreshMine();
            return;
          }
          // SCHEDULE (3071) is its own small read, like the round.
          if (ev.table === 'schedule_noms' || ev.table === 'schedule_backers') {
            void refreshSchedShown();
            return;
          }
          void get().refreshSnapshot();
          // The round is not in the snapshot (3039): it is its own small
          // fetch, and LocalRepository's coarse signal is the only realtime
          // it has. A round write in tab A reaches tab B through here.
          if (ev.table === 'rounds' || ev.table === 'round_members' || ev.table === 'round_noms' || ev.table === 'crew_events') {
            void get().refreshRound();
          }
        },
        // §2.2 patch path.
        onPatch: (p) => {
          set({ lastEventAt: Date.now() });
          if (p.t === 'round.changed') {
            void get().refreshRound();
            return;
          }
          if (p.t === 'sched.changed') {
            void refreshSchedShown();
            return;
          }
          get().applyCrewPatch(p);
        },
        // §2.4 attributed toasts + the ◆ badge recompute.
        onEvent: (e) => {
          set({ lastEventAt: Date.now() });
          get().handleCrewEvent(e);
        },
        onStatus: (state) => {
          if (state === 'subscribed') {
            // Healthy: forget the backoff, so the NEXT unrelated failure starts
            // its ladder at 2 s again rather than inheriting an old 30 s.
            retryDelay = 0;
            clearRetry();
            // SUBSCRIBED is the socket proving it is alive, which is exactly
            // what `lastEventAt` means to the focus probe in
            // `installCheckOnView`. Seeding it here is not cosmetic: the field
            // used to move ONLY on delivered rows, so a crew that had said
            // nothing since boot left it at 0 and the probe's `last &&` guard
            // read that as "nothing to be stale about" — see the comment there.
            set({ realtimeState: 'subscribed', lastEventAt: Date.now() });
            // §2.2 condition 1: SUBSCRIBED fires on connect AND on every
            // reconnect, and events lost during the gap are lost forever. The
            // refetch is therefore not optional.
            void get().refreshSnapshot();
            // The round rides the same rule (3039), and so does the clock: a
            // socket coming back is a laptop that may have slept, and the
            // wheel is replayed against the server's time, not this one's.
            void get().refreshRound();
            void get().syncClock();
            // And MY GAMES' two tables (3060), for the same reason: a score a
            // crewmate set during the gap was delivered to nobody.
            void get().refreshMine();
            // And the SCHEDULE (3071), if anything of it is on show.
            void refreshSchedShown();
            return;
          }
          set({ realtimeState: state });
          if (state === 'error') scheduleResubscribe();
        },
      });
      // The personal channel reports no status at all, so it cannot ask for
      // this itself — and it dies of the same causes as the crew channel, on
      // the same socket. Rebuilding the pair together is the honest reading of
      // the one signal we do get; rebuilding only the half that can speak would
      // leave a member's own boards stale on a live-looking app.
      unsubPersonal = get().repo.subscribePersonal(uid, {
        onChange: (ev) => {
          set({ lastEventAt: Date.now() });
          // Your own shelf or score from another tab (3060) — its own read.
          if (ev.table === 'backlog' || ev.table === 'game_scores' || ev.table === 'game_watch' || ev.table === 'board_events') {
            void get().refreshMine();
            return;
          }
          void get().refreshBoards();
        },
      });
    };

    /** §2.4: never steal attention mid-typing; queue and flush on close. */
    const composerOpen = (): boolean => {
      const s = get();
      return s.ptAddOpen || s.draft.trim().length > 0;
    };

    const emitToast = (msg: string): void => {
      if (composerOpen()) {
        if (queuedToasts.length < 6) queuedToasts.push(msg);
        return;
      }
      if (get().toasts.length >= TOAST_MAX_CONCURRENT) return;
      get().toast(msg);
    };

    const flushQueuedToasts = (): void => {
      if (composerOpen() || !queuedToasts.length) return;
      const batch = queuedToasts;
      queuedToasts = [];
      for (const m of batch.slice(0, TOAST_MAX_CONCURRENT)) get().toast(m);
    };

    return {
      repo: deps.repo,
      crewId: deps.crewId,
      resumeStore: deps.resume === undefined ? browserResumeStorage() : deps.resume,
      realtimeState: 'idle',
      lastEventAt: 0,

      boot: async () => {
        const s = get();
        const repo = s.repo;

        // 0 — the URL, which matters for exactly two shapes (D7).
        //
        // Read and SCRUBBED before anything else touches the network: the temp
        // password rides in the fragment, and the whole reason it does is that
        // fragments never reach a server. Leaving it in the address bar past
        // the moment it is read gives it to the back-forward list and to
        // anything that screenshots the tab.
        const entry =
          typeof window === 'undefined'
            ? {
              slug: null,
              invite: null,
              inviteToken: null,
              linkToken: null,
              recovery: null,
              linkError: null,
            }
            : parseEntryUrl(window.location.pathname, window.location.hash);
        if (
          // A JOIN-LINK TOKEN IS SCRUBBED TOO. It is bearer authority in the
          // address bar exactly like the temp password — anyone who replays it
          // reaches the ask screen for a party they were never given.
          (entry.invite ||
            entry.inviteToken ||
            entry.linkToken ||
            entry.recovery ||
            entry.linkError) &&
          typeof window !== 'undefined'
        ) {
          scrubFragment(window);
        }

        // 1 — session
        let session = null;
        try {
          session = await repo.currentSession();
        } catch {
          session = null;
        }

        // A PASSWORD-RESET LINK OUTRANKS WHATEVER SESSION IS LYING AROUND, for
        // the same reason an invite does: clicking it is a deliberate act that
        // names which account to be. It matters more here, though — the
        // likeliest person to open a reset link on a warm browser is somebody
        // still signed in as somebody else, and honouring the old session would
        // put them on a set-a-password screen for the wrong account.
        //
        // A FAILURE DOES NOT DISCARD THE SESSION THEY ALREADY HAD. Reset links
        // expire, and being signed out for opening a stale one — on top of not
        // getting the reset — is two bad outcomes where the link only earned
        // one. `linkDead` carries the news instead.
        let recovered = false;
        let linkDead = Boolean(entry.linkError);
        if (entry.recovery) {
          try {
            session = await repo.resumeRecovery(entry.recovery);
            recovered = true;
          } catch {
            // Expired, spent, or minted for an account that no longer has a
            // profile. Reported exactly like GoTrue's own refusal, because to
            // the reader it is the same event.
            linkDead = true;
          }
        }

        // An invite beats whatever session is lying around: the link is a
        // deliberate act and names WHICH identity to be. A failure falls
        // through to the login screen with the name filled in and the honest
        // WRONG PASSWORD, exactly like typing it.
        if (entry.invite && !session) {
          try {
            session = await repo.signIn(entry.invite.userKey, entry.invite.code);
          } catch {
            set({ sel: entry.invite.userKey });
          }
        }

        // 2 — profile / must_reset gate
        if (session) {
          set({
            session: session.userKey,
            userId: session.userId,
            pfAuthEmail: session.authEmail,
          });
          if (session.mustReset) {
            set({
              screen: 'reset',
              resetFor: session.userKey,
              resetUserId: session.userId,
              resetMode: 'first',
            });
          }
        }

        // An honoured reset link goes to the same screen with the other
        // question. The store session IS set (above) exactly as it is for a
        // first login: `reset` is one of the bare-chrome screens, so no header,
        // no nav and no board render behind it, and there is nothing on the
        // screen to navigate away with. That gate is the screen, not the
        // session — see `headerVM`'s `bare`.
        if (recovered && session) {
          set({
            screen: 'reset',
            resetFor: session.userKey,
            resetUserId: session.userId,
            resetMode: 'recover',
            rp1: '',
            rp2: '',
            resetErr: '',
          });
        }

        // 3 — everything else, tolerantly (see P2-07 in the header)
        const [roster, catalog] = await Promise.all([
          repo.loginRoster(s.crewId).catch(() => []),
          repo.fetchCatalog().catch(() => []),
        ]);
        set({ roster, catalog });
        await get().refreshSnapshot();
        if (session) await get().hydrateAfterAuth();

        // D7: `/c/<slug>` picks which crew to land on, but only if it is one of
        // mine — an unknown slug is not an error, just not a destination.
        if (session && entry.slug && !entry.invite) {
          /* AWAIT THE CREW LIST, or this can only work by luck.
             `hydrateAfterAuth` fires `loadCrews()` and deliberately does NOT
             await it — a slow crew list must not hold up the board — so by the
             time this line ran, `crews` was almost always still empty and the
             deep link silently did nothing. The feature has been here since D7
             and has probably never once worked.

             Awaited HERE rather than there, so the cost lands only on the boots
             that actually carry a `/c/<slug>`, which is the reason the fetch was
             fired and forgotten in the first place. */
          await get().loadCrews();
          const mine = get().crews.find((c) => c.slug === entry.slug);
          if (mine && mine.crewId !== get().activeCrew?.id) {
            await get().switchCrew(mine.crewId);
          }
        }

        // A signed-in account with no crew has nowhere to be: phase B's removal
        // deletes the membership rather than the account (D5), so this is
        // reachable rather than theoretical. Showing Tonight would render an
        // empty board as though the crew were quiet.
        //
        // The signal is `activeCrew` — the crew the SNAPSHOT was served from —
        // and not `crews.length`, for two reasons. It is awaited, where
        // `loadCrews` is deliberately fired and forgotten so a slow crew list
        // cannot hold up the board; basing a screen decision on it raced the
        // fetch and sent a perfectly normal account to `landing`. And it is
        // the more truthful question anyway: what matters is whether the
        // server served a crew, not whether a second request has come back yet.
        const crewless = Boolean(session) && !session?.mustReset && get().activeCrew === null;

        /* A CREWLESS ACCOUNT MAY BE WAITING RATHER THAN STRANDED (3056).
           Until now `crewless` meant one thing — start a party — because the
           only way to have no party was to be removed from your last. Asking to
           join through a link is a second way, and it looks identical from
           here: a session, no active crew.

           ASKED ONLY WHEN `crewless`, so the ordinary boot pays nothing for a
           feature it will never hit. And `loadAskStatus` SWALLOWS ITS OWN
           FAILURE by design — this sits on the path of every account with no
           party, and a slow or broken status call must fall through to the old
           screen rather than hang a boot on it. */
        const waitingOnLeader = crewless ? await get().loadAskStatus() : false;
        // Signed out lands on LANDING (DESIGN-CHANGELOG 2026-08-18); LOG IN is
        // one tap behind it, and an invite link skips both.
        //
        // The exception is an invite that FAILED: the identity is already in
        // the form and the honest WRONG PASSWORD belongs beside it, so bouncing
        // to landing would throw both away.
        const inviteFailed = Boolean(entry.invite) && !session;
        // A DEAD EMAILED LINK MUST SAY SO. Left to fall through, an expired
        // reset dropped the reader on the landing screen with nothing said —
        // and the conclusion a person draws from that is "the link did
        // nothing", which sends them to press it again rather than to ask for
        // a new one. Signed out it becomes the login form's error line, where
        // FORGOT PASSWORD? is one tap away; signed in it is a toast, because
        // the board behind it is still where they belong.
        if (linkDead && !recovered) {
          const why = (entry.linkError || 'THAT LINK HAS EXPIRED').toUpperCase();
          if (session) get().toast(why, true);
          else set({ loginErr: why });
        }
        const scr = recovered
          ? 'reset'
          : session
            ? session.mustReset
              ? 'reset'
              : crewless
                ? waitingOnLeader
                  ? 'waiting'
                  : 'landing'
                // MY GAMES is where a signed-in boot lands (2026-09-03).
                : 'mine'
            : inviteFailed || linkDead
              ? 'login'
              : 'landing';
        /* RESUME — a reload comes back to the screen it left (store/resume.ts).
           Consulted ONLY when the answer above was the default MY GAMES, and
           never over an invitation or a join link: those are what THIS load
           asked for, decided just below, and a remembered BACKLOG must not
           race them. `applyResume` re-checks the account and the crew against
           what the server just served, so a stale snapshot is a no-op.

           READ BEFORE `booted` FLIPS, applied after. The writer in resume.ts
           is live during boot and wakes on `booted`; if the read came after
           the `set` below, the writer would already have overwritten the
           snapshot with the MY GAMES it had just been told about. Found in a
           browser, not by the unit test that detached the writer first. */
        const askToken = entry.linkToken || rememberedAsk();
        const resumable = scr === 'mine' && get().screen !== 'detail' && !entry.inviteToken && !askToken;
        const resumeSnap = resumable ? readResume(get().resumeStore, Date.now()) : null;

        set({ screen: get().screen === 'detail' ? 'detail' : scr, ready: true, booted: true });

        if (resumeSnap) applyResume(get(), resumeSnap);

        // A 3026 INVITATION OVERRIDES ALL OF THAT, and it is decided last on
        // purpose: everything above answers "who is this and where do they
        // belong", which the JOIN screen needs to have settled before it can
        // tell an invitee they are signed in as somebody else. It applies
        // whether or not there is a session — a member of one crew being
        // invited to a second is the common case, not an edge one.
        if (entry.inviteToken) {
          void get().openJoin(entry.inviteToken);
        }

        /* A 3056 JOIN LINK OVERRIDES IT TOO, on the same reasoning and decided
           beside it: who this is has to be settled before the ASK screen can
           tell somebody they are already in the party, or offer to ask as
           them. Honoured with or without a session — a member of one party
           following a friend's link to a second is the ordinary case. */
        /* THE FRAGMENT IS GONE AFTER THE FIRST LOAD, so a remembered token is
           the only way a reload — or a trip through the login screen that
           reloads the document — can put somebody back where they were. Unlike
           an invitation there is no email to reopen: the link came from a
           friend's chat, and losing it mid-flow is unrecoverable.

           The fragment wins when both are present: it is what this navigation
           actually asked for, and it may be a DIFFERENT party's link than the
           one abandoned earlier. (`askToken` is read above, beside the resume
           check that has to know about it.) */
        if (askToken) {
          void get().openAsk(askToken);
        }

        // 4–6
        get().installCheckOnView();
        void get().revalidateVisible();
        // Discover's default view is a server-ranked page now, so it has to be
        // asked for. Fired here rather than on first paint of the screen so the
        // grid is populated before the user navigates to it — and after the
        // catalog, because a failed feed falls back to exactly that.
        get().restartFeed();
      },

      /** Steps 3–4 for a session that appeared after boot (login / first reset). */
      hydrateAfterAuth: async () => {
        const s = get();
        const uid = s.userId || s.session;
        if (!uid) return;
        // P4b-01: the catalog is readable only to `authenticated` (migration
        // 1000 grants nothing on `v_games` to `anon`), so the pre-auth pass in
        // `boot()` above always comes back empty on a cold sign-in. Without a
        // refetch here every screen renders zero games until the user reloads
        // — the list rows exist but have no game to join to.
        //
        // THE EMPTINESS GUARD BELOW IS ONLY CORRECT BECAUSE EVERY CONTEXT
        // TRANSITION CLEARS THE CATALOG (2026-08-22). `games_bootstrap` shapes
        // its answer around the caller — the global head plus THEIR crew's
        // games — so a catalog is only reusable inside the identity-and-crew
        // it was fetched in. Sign-out, crew switch/create/remove and invite
        // accept all blank it (`clearedCrewState` / `signOut`); the one warm
        // case that legitimately skips is a signed-in boot, where step 3 just
        // fetched it AS this account. When this guard skipped after an ACCOUNT
        // SWITCH, the new crew's list joined against the old account's catalog
        // and Tonight silently dropped half its games (Marcus, mobile,
        // production, the day after release).
        const [boards, steamLink, catalog] = await Promise.all([
          s.repo.fetchMyBoards(uid, s.gotyYear).catch(() => null),
          s.repo.fetchSteamLink(uid).catch(() => null),
          s.catalog.length ? Promise.resolve(null) : s.repo.fetchCatalog().catch(() => null),
        ]);
        if (catalog?.length) set({ catalog });
        // Same reason as the pre-auth pass above: `discover_page` is granted to
        // `authenticated` only, so the boot-time call came back empty on a cold
        // sign-in and Discover would stay on the fallback path until a reload.
        get().restartFeed();
        await get().refreshSnapshot();
        if (boards) {
          const k = get().session || uid;
          set((st) => ({
            potentials: { ...st.potentials, [k]: boards.potentials },
            tiers: { ...st.tiers, [k]: boards.tiers },
            tierOrder: { ...st.tierOrder, [k]: boards.tierOrder },
            skips: { ...st.skips, [k]: boards.skips },
            intOrder: { ...st.intOrder, [k]: boards.intOrder },
            intSkips: { ...st.intSkips, [k]: boards.intSkips },
          }));
        }
        set({ steamLink });
        // Where their Steam library check stands (3070). NOT awaited — it draws
        // one button's label — and only asked for a linked member. This is also
        // what lets a tab the phone evicted come back telling the truth: if a
        // pass is still running, the read finds it and the progress line resumes.
        if (steamLink?.linked) void get().refreshSteamBackfill();

        // MY GAMES' backlog and the party's scores (3060). After the snapshot,
        // because the crew read maps `user_id` → key through the roster the
        // snapshot just delivered. AWAITED, like `fetchMyBoards` above and
        // for the same reason: MY GAMES is the screen a sign-in lands on, and
        // its first paint should be the shelf, not an empty shelf that fills
        // in a beat later. Both reads swallow their own failures.
        await get().refreshMine();

        // Which crews this account belongs to. Not awaited into the critical
        // path — nothing renders it until phase D's switcher, and a slow or
        // failed crew list must not hold up the board.
        void get().loadCrews();

        // The open round and the shared clock (3039). Neither is on the
        // critical path: the board renders without them, and the chip appears
        // when the answer lands.
        void get().refreshRound();
        void get().syncClock();

        subscribeAll(uid);

        /* THE ART PASS, and it was MISSING here until 2026-08-22. `boot()`
           fires `revalidateVisible` at step 5 and the focus handler fires it on
           tab-return — but every warm transition funnels through THIS function
           with the list freshly loaded and nothing ever asking for its tiles'
           art. The tiles that survived were the ones whose appid-guessable
           legacy URLs still resolve; a game whose art lives only at a
           content-hashed path — obtainable from a GetItems response and
           nowhere else — rendered a permanent checkerboard after any sign-in
           or crew switch, deterministically, and healed on reload because a
           reload is a boot (Marcus, production: Heave Ho 2, whose art sat in
           steam_facts the whole time). Fire-and-forget for the same reason
           `loadCrews` is: art must not hold up the board, and `giFlush` falls
           back to the crew-shared DB art when the Worker is unreachable. */
        void get().revalidateVisible();
      },

      /**
       * Rebuild both channels. Idempotent — `subscribeCrew` supersedes whatever
       * it finds — and safe to call from anywhere, because a lost session or a
       * torn-down store makes it a no-op.
       */
      resubscribe: () => {
        if (retriesOff) return;
        const uid = get().userId || get().session;
        if (!uid) return;
        subscribeAll(uid);
      },

      refreshSnapshot: async () => {
        const s = get();
        const epoch = ++snapshotEpoch;
        snapshotInFlight++;
        try {
          const snap = await s.repo.fetchCrewSnapshot(s.crewId);
          // A newer snapshot already landed: this one is stale, drop it. NOT a
          // `return` — the replay below has to run whatever happened here.
          if (epoch === snapshotEpoch) {
            set({
              users: snap.members,
              list: snap.entries,
              pts: snap.playtests,
              videos: snap.videos,
              // D2: the crew the server ACTUALLY served, which is the only
              // thing that can tell a live session it is no longer looking at
              // the crew it thinks it is — deleted underneath it, or removed
              // from it, both of which leave my_crew_id() quietly answering
              // with a different crew.
              activeCrew: snap.crew,
            });
          }
        } catch {
          /* pre-auth under RLS, or offline — leave what we have */
        } finally {
          snapshotInFlight--;
        }
        // §2.2: replay the patches that arrived while the fetch was open, so an
        // in-flight optimistic write is not silently rolled back.
        //
        // OUTSIDE the try/finally, and that placement is the whole point:
        // `applyCrewPatch` re-queues anything handed to it while a snapshot is
        // open, so a replay run before the counter came down just put every
        // patch straight back on the queue — the array grew forever and not one
        // patch was ever applied. A still-positive counter means another
        // snapshot is open and will drain the queue itself.
        if (snapshotInFlight === 0 && pendingPatches.length > 0) {
          const replay = pendingPatches;
          pendingPatches = [];
          for (const p of replay) get().applyCrewPatch(p);
        }
      },

      /**
       * The backlog and the scores, refetched whole (3060). Single-flight with
       * a trailing pass, for the reason `refreshBoards` below spells out: the
       * personal channel echoes per row, and a refetch storm is a network
       * error wearing a costume. The two reads are independent, so one failing
       * leaves the other's answer standing — a stale shelf is better than no
       * shelf, and vice versa.
       */
      refreshMine: () => {
        if (mineInFlight) {
          mineAgain = true;
          return mineRun;
        }
        mineInFlight = true;
        mineRun = refreshMineRun();
        return mineRun;
      },

      /**
       * SINGLE-FLIGHT, with a trailing pass. Both properties are load-bearing.
       *
       * The personal channel reports one event PER ROW, and a board write is
       * not always one row: `setIntAt`'s fallback rewrites the whole interest
       * order, so an N-game board echoes N times. This used to open a
       * `fetchMyBoards` per echo — two GETs apiece, all at once — and rating a
       * handful of games quickly put enough of them in flight to exhaust the
       * browser's socket pool outright:
       *
       *     GET .../interest_order?select=*&user_id=eq.… net::ERR_INSUFFICIENT_RESOURCES
       *
       * which is a refetch storm wearing a network error's clothes. The
       * concurrency cap is the fix; `refreshSnapshot` has had one since §2.2.
       *
       * The trailing pass is what keeps the collapse honest. Events that
       * arrived while a fetch was open are not IN that fetch, so the loop runs
       * once more rather than trusting a response that predates them — the
       * same reason `refreshSnapshot` replays `pendingPatches` on the way out.
       */
      refreshBoards: async () => {
        if (boardsInFlight) {
          boardsAgain = true;
          return;
        }
        boardsInFlight = true;
        try {
          do {
            boardsAgain = false;
            // Never land on top of an open optimistic write. A refetch that
            // does rewinds two things at once: the store (the §5.5 patch that
            // has not been confirmed yet) and `SupabaseRepository`'s board
            // cache — and that cache is exactly what `setIntAt` diffs to decide
            // it is replaying ONE move. Rewound, it sees a multi-item change,
            // gives up, and rewrites every row of the order, which is what
            // produces the N echoes above. Waiting costs nothing here: every
            // one of these passes is background reconciliation, never
            // something a tap is waiting on.
            await whenIdle();
            const s = get();
            const uid = s.userId || s.session;
            const k = s.session || uid;
            if (!uid || !k) return;
            const [b, years] = await Promise.all([
              s.repo.fetchMyBoards(uid, s.gotyYear),
              // The year row (AA phase 5) derives from what exists.
              s.repo.fetchGotyYears(uid).catch(() => null),
            ]);
            if (years) set({ gotyYears: years });
            set((st) => ({
              potentials: { ...st.potentials, [k]: b.potentials },
              tiers: { ...st.tiers, [k]: b.tiers },
              tierOrder: { ...st.tierOrder, [k]: b.tierOrder },
              skips: { ...st.skips, [k]: b.skips },
              intOrder: { ...st.intOrder, [k]: b.intOrder },
              intSkips: { ...st.intSkips, [k]: b.intSkips },
            }));
          } while (boardsAgain);
        } catch {
          /* transient — the next SUBSCRIBED or focus pass picks it up */
        } finally {
          boardsInFlight = false;
          boardsAgain = false;
        }
      },

      /**
       * §2.2 patch application + §2.3 echo suppression.
       *
       * Every branch is an idempotent upsert keyed by a server id, so replaying
       * a patch — which is exactly what a reconnect does — converges rather than
       * duplicating. That property is what makes the `pendingPatches` replay
       * above safe.
       */
      applyCrewPatch: (p) => {
        // A snapshot is open: hold the patch and replay it on top (§2.2).
        if (snapshotInFlight > 0) {
          pendingPatches.push(p);
          return;
        }

        const meKey = get().session;
        const key = meKey ? patchEchoKey(p, meKey) : null;
        const mine = key ? lookupInFlight(key) : undefined;
        if (mine) {
          // §2.3: our own write coming back. Whether it is OURS is asked of
          // what our writes left behind (`InFlightWrite.echoes`), not of the
          // live store — a snapshot refetch or a newer tap on the same row
          // moves the store out from under us, and asking there turned our own
          // echo into an accusation.
          //
          // Only an OPEN write can be raced. Once every write on this key has
          // settled, an unrecognised row is just ordinary crew traffic
          // arriving late, which has never been worth a toast.
          const asked = mine.echoes.some((after) => patchMatchesStore(after, p));
          if (!asked && mine.open > 0) get().toast('✕ SOMEONE ELSE CHANGED THAT', true);
          // The no-op short circuit, and it has to be asked of the LIVE store:
          // recognising the echo does not mean the store still holds it. That
          // is exactly the case a stale snapshot creates, and returning on the
          // strength of `asked` alone is what dropped the write on the floor.
          if (patchMatchesStore(get(), p)) return;
        }

        switch (p.t) {
          case 'catalog.upsert': {
            set((st) => ({
              catalog: st.catalog.some((g) => g.appid === p.game.appid)
                ? st.catalog.map((g) => (g.appid === p.game.appid ? p.game : g))
                : [...st.catalog, p.game],
            }));
            return;
          }
          case 'entry.upsert': {
            set((st) => ({
              list: st.list.some((e) => e.entryId === p.entry.entryId)
                ? st.list.map((e) => (e.entryId === p.entry.entryId ? mergeEntry(e, p.entry) : e))
                : // The optimistic placeholder from `quickAdd` shares the game id
                  // but not the entry id; replace it rather than doubling up.
                  st.list.some((e) => e.id === p.entry.id)
                  ? st.list.map((e) => (e.id === p.entry.id ? mergeEntry(e, p.entry) : e))
                  : [...st.list, p.entry],
            }));
            return;
          }
          case 'entry.remove': {
            set((st) => ({ list: st.list.filter((e) => e.entryId !== p.entryId) }));
            return;
          }
          case 'vote.set': {
            set((st) => ({
              list: st.list.map((e) => {
                if (e.entryId !== p.entryId) return e;
                const votes = { ...e.votes };
                // Absence is the "no call" state (§1.5).
                if (p.value) votes[p.userKey] = p.value;
                else delete votes[p.userKey];
                return { ...e, votes };
              }),
            }));
            return;
          }
          case 'comment.upsert': {
            set((st) => ({
              list: st.list.map((e) => {
                if (e.entryId !== p.entryId) return e;
                const has = e.comments.some((c) => c.id === p.comment.id);
                const comments = has
                  ? e.comments.map((c) => (c.id === p.comment.id ? p.comment : c))
                  : [...e.comments, p.comment];
                comments.sort((a, b) => a.at - b.at);
                return { ...e, comments };
              }),
            }));
            return;
          }
          case 'comment.remove': {
            set((st) => ({
              list: st.list.map((e) =>
                e.entryId === p.entryId
                  ? { ...e, comments: e.comments.filter((c) => c.id !== p.commentId) }
                  : e,
              ),
            }));
            return;
          }
          case 'playtest.upsert': {
            set((st) => ({
              pts: st.pts.some((x) => x.id === p.playtest.id)
                ? st.pts.map((x) => (x.id === p.playtest.id ? mergePt(x, p.playtest) : x))
                : [...st.pts, p.playtest],
            }));
            return;
          }
          case 'playtest.remove': {
            set((st) => ({ pts: st.pts.filter((x) => x.id !== p.playtestId) }));
            return;
          }
          case 'pt.int': {
            set((st) => ({
              pts: st.pts.map((x) => {
                if (x.id !== p.playtestId) return x;
                const int = { ...x.int };
                if (p.at === null) delete int[p.userKey];
                else int[p.userKey] = p.at;
                return { ...x, int };
              }),
            }));
            return;
          }
          case 'pt.status': {
            set((st) => ({
              pts: st.pts.map((x) => {
                if (x.id !== p.playtestId) return x;
                const stt = { ...x.st };
                if (p.status === null) delete stt[p.userKey];
                else stt[p.userKey] = p.status;
                return { ...x, st: stt };
              }),
            }));
            return;
          }
          case 'member.upsert': {
            set((st) => ({
              users: st.users.some((u) => u.k === p.user.k)
                ? st.users.map((u) => (u.k === p.user.k ? mergeUser(u, p.user) : u))
                : [...st.users, p.user],
            }));
            return;
          }
          case 'member.remove': {
            set((st) => ({
              users: st.users.filter((u) => u.k !== p.userKey),
              // A nulled attribution renders as the FORMER FRIEND chip.
              list: st.list.map((e) => (e.by === p.userKey ? { ...e, by: '' } : e)),
              videos: st.videos.map((v) => (v.by === p.userKey ? { ...v, by: '' } : v)),
            }));
            return;
          }
          case 'video.upsert': {
            set((st) => {
              // Two identities to reconcile: the server id, and — for our own
              // echo — the `pending_` placeholder, which shares (appid, key)
              // but not the id. Matching on both is what stops a submit from
              // rendering the same clip twice for a beat.
              const has = st.videos.some((v) => v.id === p.video.id);
              if (has) {
                return { videos: st.videos.map((v) => (v.id === p.video.id ? p.video : v)) };
              }
              const placeholder = st.videos.findIndex(
                (v) => v.appid === p.video.appid && v.key === p.video.key,
              );
              if (placeholder >= 0) {
                return {
                  videos: st.videos.map((v, i) => (i === placeholder ? p.video : v)),
                };
              }
              return { videos: [...st.videos, p.video] };
            });
            return;
          }
          case 'video.remove': {
            set((st) => ({ videos: st.videos.filter((v) => v.id !== p.videoId) }));
            return;
          }
        }
      },

      /**
       * §2.4. All cross-member notification derives from `crew_events`, so there
       * is one code path, one throttle, and the DB triggers guarantee the copy
       * matches what actually happened.
       */
      handleCrewEvent: (e) => {
        // Drop our own: the optimistic path already toasted it.
        if (e.actorKey && e.actorKey === get().session) return;

        // P6 F-4. An actor-less event is either a system event we have copy for
        // (the nightly scan's opens/closes/waves) or one we do not — the second
        // kind still bumps the ◆ badge through `onEvent`, it just does not toast.
        if (!e.actorKey && !SYSTEM_EVENT_KINDS.has(e.kind)) return;

        const msg = eventToast(e, get().user(e.actorKey)?.name ?? e.actorKey);
        if (!msg) return;

        // No actor means no "CHELSEA MADE 4 CALLS" to collapse into: the crew
        // did not do this, the scraper did. It goes straight out (still behind
        // the composer guard and the 3-toast cap in `emitToast`).
        if (!e.actorKey) {
          emitToast(msg);
          return;
        }

        // Coalesce per actor over a 1500 ms window.
        const open = toastWindow.get(e.actorKey);
        if (open) {
          open.n += 1;
          return;
        }
        const timer = setTimeout(() => {
          const w = toastWindow.get(e.actorKey);
          toastWindow.delete(e.actorKey);
          if (w && w.n >= TOAST_COLLAPSE_AT) {
            emitToast((get().user(e.actorKey)?.name ?? e.actorKey).toUpperCase() + ' MADE ' + w.n + ' CALLS');
          } else {
            emitToast(msg);
          }
        }, TOAST_COALESCE_MS);
        toastWindow.set(e.actorKey, { n: 1, timer });
      },

      /**
       * §5.4's SWR pass, over the rows a member is ACTUALLY LOOKING AT.
       *
       * It used to read `catalog.slice(0, 12)`, and that had quietly stopped
       * describing any screen in the app. Discover renders `feedRows` in feed
       * mode and `live` in browse mode — `selectors.ts#discoverVM` picks between
       * exactly those three arrays — and the catalog is only what the FALLBACK
       * path renders. So on the app's main surface every focus revalidated
       * twelve rows that were not on screen and none of the rows that were,
       * which together with `queueAssets`' art dedupe (see
       * `slices/steamFacts.ts#revalidateSurface`) is why nothing on Discover
       * ever refreshed while a member looked at it.
       *
       * The crew list stays in the pass, unchanged: those rows are on Tonight
       * and Interests, they are the ones with money on them, and they are shared.
       *
       * `GI_MAX_BATCH` bounds the surface head because that is one items request
       * — the budget a focus pass should cost. A member who has pressed LOAD
       * MORE five times has 300 rows loaded and is looking at a dozen of them;
       * refreshing all 300 on every alt-tab would spend six requests to update
       * rows nobody can see. The rest keep whatever the page fetch gave them
       * until they are asked for again.
       *
       * THE SURFACE GOES FIRST, AND THE ORDER IS THE WHOLE POINT OF THE ARRAY.
       * `revalidateSurface` takes the head of what it is handed for BOTH of its
       * budgets — `due.slice(0, CCU_SURFACE_MAX)` for the live counts,
       * `due.slice(0, GI_MAX_BATCH)` for the immediate items request — so this
       * list is a priority order wearing the clothes of a concatenation. With
       * the crew list first, a crew tracking twelve games spent the entire
       * 12-slot CCU budget on list rows and the Discover surface got ZERO live
       * counts on every focus pass, which is the exact opposite of the reason
       * `CCU_SURFACE_MAX` is documented for ("the head of the grid is the part
       * somebody is actually looking at"), and Discover's own effect does not
       * re-fire to correct it because the row ids did not change.
       *
       * Chosen over interleaving because the two budgets fail differently and
       * only one of them can starve. The items cap has a TAIL: everything past
       * `GI_MAX_BATCH` goes through `giQ`/`giDrain` and lands one 500 ms
       * coalesce window later, so the crew list is DELAYED, never dropped. The
       * CCU cap has no tail at all — it is one Worker invocation, fanning out one
       * upstream call per appid, and appids past the twelfth are simply not
       * asked about. A hard cap with no second chance has to point at what is on
       * screen. Interleaving would have split the twelve and made both halves
       * arbitrary; this way the losing side is the one that loses nothing.
       *
       * Known and accepted: `surface` is whichever array Discover would render,
       * not whichever screen is open, so a focus pass taken on Tonight still
       * ranks Discover's rows first. That costs the list rows their live counts
       * on that pass — a caption nothing renders yet — and costs them nothing at
       * all on the items side. Making the pass screen-aware is a separate change
       * with its own reasons; it is not a reason to keep the ordering that
       * starves the app's default screen.
       */
      revalidateVisible: async () => {
        const s = get();
        const listAppids = s.list
          .map((e) => s.gById(e.id))
          .filter((g): g is NonNullable<typeof g> => !!g)
          .map((g) => g.appid);
        // MY BACKLOG rides with the crew list, for the same reason the list is
        // here at all: nothing else ever asks for its art. A shelved game is
        // routinely on neither the party list nor the Discover head — that is
        // what a backlog is FOR — and `artCands` can only GUESS the legacy CDN
        // shapes for a game whose assets were never fetched, which 404 for
        // anything recent. Marcus, production 2026-09-05: "Librarian: Tidy Up
        // the Arcane Library!" (appid 4197610, every legacy shape 404) sat as
        // a checkerboard on his backlog while its art was one GetItems call
        // away. `refreshMine` is awaited before this pass on every warm
        // transition, so the shelf is loaded by the time this reads it.
        const me = s.me();
        const shelfAppids = (me ? s.backlog[me.k] ?? [] : [])
          .map((e) => s.gById(e.id))
          .filter((g): g is NonNullable<typeof g> => !!g)
          .map((g) => g.appid);
        const surface = browseMode(s) ? s.live : feedMode(s) ? s.feedRows : s.catalog;
        const head = surface.slice(0, GI_MAX_BATCH).map((g) => g.appid);
        // `new Set` keeps FIRST occurrence, so an appid on both lists is ranked
        // by the surface — which is the one asserting it is on screen.
        const appids = [...new Set([...head, ...listAppids, ...shelfAppids])].filter((a) => a > 0);
        // `revalidateSurface`, not `revalidate`: the per-appid floor lives there,
        // and so does the live-count overlay this pass is the main caller of.
        await s.revalidateSurface(appids);
      },

      /**
       * §5.4 check-on-view. Note what does NOT happen here: focus alone never
       * advances `lastOkAt`. It only *starts* a revalidation whose outcome does.
       */
      installCheckOnView: () => {
        if (typeof window === 'undefined') return () => undefined;
        const onFoc = () => {
          if (typeof document !== 'undefined' && document.hidden) return;
          get().bumpTick();
          flushQueuedToasts();
          // THE COME-BACK HALF OF THE STEAM LIBRARY CHECK (3070). The pass runs on
          // the server and finishes without this tab; a member who pressed
          // auto-fill, was told to wait, and went to another app is exactly who
          // it was built for. Re-read the moment they return — BEFORE both
          // throttle gates below, which exist to ration crew refetches and would
          // otherwise leave them staring at a stale "140 OF 400" for up to
          // fifteen seconds. One row, no Steam call; if the pass finished while
          // they were away, this is what opens the picker they asked for.
          {
            const st = get();
            if (st.session && (st.gotyFillWanted !== null || st.steamBackfill?.waiting)) {
              void st.refreshSteamBackfill();
            }
          }
          // §2.2 condition 3: cheap paranoia against a silently-dead socket.
          //
          // THE TWO WAYS THIS USED TO SKIP THE CASE IT EXISTS FOR. Both showed
          // up as the same report: come back to a backgrounded tab and the ◆
          // What's New badge is still yesterday's count until a manual reload.
          // The badge is `newForMe(list, …)` (selectors.ts#headerVM), so it is
          // only ever as fresh as `list` — nothing recomputes it on view.
          //
          //  1. `lastEventAt &&`. The field moved only on delivered rows, and a
          //     six-person crew is silent for hours, so a tab that had received
          //     nothing since boot sat at 0 and the guard read that as "not
          //     stale" — the probe never ran at all. `onStatus` seeds it now,
          //     and 0 is treated below as "never confirmed alive", which probes.
          //  2. Staleness alone. A socket the phone froze on app-switch is
          //     already known-bad by `realtimeState`, and waiting out the 60 s
          //     window (or a Phoenix heartbeat's reconnect, which is what
          //     eventually re-fires SUBSCRIBED) is 30-odd seconds of showing a
          //     number we know is wrong. Anything but `subscribed` resyncs on
          //     sight.
          //
          // `lastResyncAt` is what keeps that widening honest — see its
          // declaration for why the probe cannot throttle itself.
          const st = get();
          const now = Date.now();
          const socketOk = st.realtimeState === 'subscribed';
          const quietFor = st.lastEventAt ? now - st.lastEventAt : Infinity;
          if (
            st.session &&
            now - lastResyncAt > REVALIDATE_MIN_MS &&
            (!socketOk || quietFor > STALE_SOCKET_MS)
          ) {
            lastResyncAt = now;
            void get().refreshSnapshot();
            void get().refreshBoards();
            void get().refreshMine();
            // The round is NOT in the snapshot (3039) — it is its own small
            // fetch, and this probe was the one resync path that did not make
            // it. Every other one does: SUBSCRIBED, `hydrateAfterAuth`, and a
            // `round.changed` patch. So the gap showed only where there is no
            // socket left to carry it — lock the phone, a third member joins,
            // come back to the tab: the crew list and the boards were current
            // and the round's members were yesterday's until a manual reload
            // (Marcus, testing, 2026-08-27).
            void get().refreshRound().then(() => {
              // The wheel is posed against `serverNow()`, so a device that has
              // just woken needs its offset re-sampled before that pose can be
              // trusted — the same reason SUBSCRIBED syncs the clock. Chained
              // and conditional because three round trips are only worth
              // spending when there is a round to pose, and the round worth
              // posing may be the one that started while we were away.
              if (get().round) void get().syncClock();
            });
            // A refetch makes the DATA right once; only a channel makes it stay
            // right. `error` is the terminal state — nothing downstream of us
            // will rebuild it — so a member who has come back to the tab is the
            // best excuse there is to stop waiting out the backoff and try now.
            //
            // Only `error`. `reconnecting` is phoenix mid-rejoin, and tearing
            // that down to start over would throw away work that was about to
            // succeed.
            if (st.realtimeState === 'error') get().resubscribe();
          }
          // The ago-tick and the queued toasts above still run on every fire;
          // the SWR pass and the resync are floored, each on its own clock —
          // they cost different things and neither should be able to eat the
          // other's budget.
          if (now - lastFocusRevalAt < REVALIDATE_MIN_MS) return;
          lastFocusRevalAt = now;
          void get().revalidateVisible();
        };
        window.addEventListener('focus', onFoc);
        document.addEventListener('visibilitychange', onFoc);
        if (agoT) clearInterval(agoT);
        agoT = setInterval(() => {
          if (get().session) {
            get().bumpTick();
            flushQueuedToasts();
          }
        }, AGO_TICK_MS);

        removeListeners = () => {
          window.removeEventListener('focus', onFoc);
          document.removeEventListener('visibilitychange', onFoc);
          if (agoT) clearInterval(agoT);
          agoT = null;
        };
        return removeListeners;
      },

      teardown: () => {
        removeListeners?.();
        removeListeners = null;
        // Before the unsubs: a pending retry must not outlive the store and
        // rebuild the channels we are about to drop.
        retriesOff = true;
        clearRetry();
        retryDelay = 0;
        unsubCrew?.();
        unsubPersonal?.();
        unsubCrew = null;
        unsubPersonal = null;
        for (const w of toastWindow.values()) clearTimeout(w.timer);
        toastWindow.clear();
        queuedToasts = [];
        pendingPatches = [];
        clearInFlight();
        lastFocusRevalAt = 0;
        lastResyncAt = 0;
        get().cancelRevalidate();
        // The Steam library-check poller is an interval too, and one that
        // outlives the store would keep asking a disposed repository.
        get().clearSteamBackfill();
      },
    };

    // Hoisted: declared after the return so `refreshMine` above can call it
    // and the single-flight flags stay private to this closure.
    async function refreshMineRun(): Promise<void> {
        try {
          do {
            mineAgain = false;
            await whenIdle();
            const s = get();
            const uid = s.userId || s.session;
            const k = s.session || uid;
            if (!uid || !k) return;
            const [bk, sc, sd, wt, ev] = await Promise.all([
              s.repo.fetchBacklog().catch(() => null),
              s.repo.fetchScores().catch(() => null),
              // Recommendations (3062) ride the same read: same triggers, same
              // "the party did something" signal, same stray-game problem.
              s.repo.fetchSends().catch(() => null),
              // The watch and the board (3063), likewise.
              s.repo.fetchWatch().catch(() => null),
              s.repo.fetchBoard().catch(() => null),
            ]);
            if (wt) set({ watch: wt });
            if (ev) set({ board: ev });
            if (bk) set((st) => ({ backlog: { ...st.backlog, [k]: bk } }));
            // Replaced whole, like the scores: a revoked send is a row that is
            // simply not in the next answer.
            if (sd) set({ sends: sd });
            if (sc) {
              // The crew read is authoritative for everybody it names — and for
              // everybody it does NOT name: a member who cleared a score, or a
              // member who left, drops out of the map on the next read.
              const scores: Record<string, Record<string, ScoreRec>> = {};
              for (const r of sc) (scores[r.userKey] ??= {})[String(r.id)] = r.prev === null ? { v: r.v, at: r.at } : { v: r.v, at: r.at, prev: r.prev };
              set({ scores });
            }
            // THE GAMES THESE ROWS NAME HAVE TO BE DRAWABLE. `games_bootstrap`
            // carries the backlog and the party's scores since 3061, so on a
            // fresh boot nothing is missing — but a crewmate can score a game
            // this session has never fetched (the crew channel delivers the
            // row, not the game), and a bundle can meet a database that
            // predates 3061. Either way `backlogVM` and the notice board would
            // silently drop the row (Marcus, staging, 2026-09-04: two shelved
            // games "do not show" after a refresh). Ask the bootstrap for the
            // strays by appid; it answers with its whole set plus them, and ONLY
            // the strays are adopted — the same rule `fetchOneGame` follows.
            // Replacing the catalog wholesale here would move Discover's
            // fallback universe under a screen that is showing it, for a read
            // that was only ever about a handful of rows.
            const st = get();
            const named = new Set<number>();
            for (const e of bk ?? []) named.add(Number(e.id));
            for (const r of sd ?? []) named.add(Number(r.gid));
            for (const k of Object.keys(wt ?? {})) named.add(Number(k));
            for (const e of ev ?? []) named.add(Number(e.gid));
            for (const r of sc ?? []) named.add(Number(r.id));
            const missing = [...named].filter((id) => id > 0 && !st.gById(id));
            if (missing.length) {
              const want = new Set(missing);
              const games = await st.repo.fetchCatalog(missing).catch(() => null);
              const strays = (games ?? []).filter((g) => want.has(g.appid));
              if (strays.length) {
                set((cur) => {
                  const have = new Set(cur.catalog.map((g) => g.appid));
                  const add = strays.filter((g) => !have.has(g.appid));
                  return add.length ? { catalog: [...cur.catalog, ...add] } : {};
                });
              }
            }
          } while (mineAgain);
        } catch {
          /* transient — the next SUBSCRIBED or focus pass picks it up */
        } finally {
          mineInFlight = false;
          mineAgain = false;
        }
    }
  };

// ---------------------------------------------------------------------------
// patch helpers
// ---------------------------------------------------------------------------

/**
 * An INSERT patch carries the bare row: votes and comments arrive as their own
 * events. Merging rather than replacing keeps whatever is already local.
 */
function mergeEntry(cur: ListEntry, next: ListEntry): ListEntry {
  return {
    ...next,
    votes: Object.keys(next.votes).length ? next.votes : cur.votes,
    comments: next.comments.length ? next.comments : cur.comments,
  };
}

/** Same reasoning for playtests: interest and status are separate tables. */
function mergePt(cur: Playtest, next: Playtest): Playtest {
  return {
    ...next,
    int: Object.keys(next.int).length ? next.int : cur.int,
    st: Object.keys(next.st).length ? next.st : cur.st,
  };
}

/** `seen` is self-only under RLS, so a profiles patch must not blank it. */
function mergeUser(cur: User, next: User): User {
  return { ...next, seen: next.seen.length ? next.seen : cur.seen };
}

/**
 * §2.3: does the incoming server row already agree with what one of our
 * optimistic writes put in the store? If it does, the echo is a no-op. If no
 * write of ours produced it, somebody raced us and the caller toasts.
 *
 * Takes the four data arrays rather than the whole store precisely so it can
 * be pointed at a `Snapshot` taken when a write went out, instead of at
 * whatever the store happens to hold now.
 */
function patchMatchesStore(s: Pick<QdStore, 'list' | 'pts' | 'users' | 'videos'>, p: CrewPatch): boolean {
  switch (p.t) {
    case 'vote.set': {
      const e = s.list.find((x) => x.entryId === p.entryId);
      if (!e) return false;
      return (e.votes[p.userKey] ?? null) === p.value;
    }
    case 'entry.upsert':
      return s.list.some((e) => e.id === p.entry.id);
    case 'entry.remove':
      return !s.list.some((e) => e.entryId === p.entryId);
    case 'comment.upsert': {
      const e = s.list.find((x) => x.entryId === p.entryId);
      return !!e && e.comments.some((c) => c.t === p.comment.t && c.by === p.comment.by);
    }
    case 'comment.remove': {
      const e = s.list.find((x) => x.entryId === p.entryId);
      return !e || !e.comments.some((c) => c.id === p.commentId);
    }
    case 'pt.int': {
      const x = s.pts.find((y) => y.id === p.playtestId);
      if (!x) return false;
      return p.at === null ? !x.int[p.userKey] : !!x.int[p.userKey];
    }
    case 'pt.status': {
      const x = s.pts.find((y) => y.id === p.playtestId);
      if (!x) return false;
      const cur = x.st[p.userKey];
      return p.status === null ? !cur : !!cur && cur.s === p.status.s;
    }
    case 'playtest.remove':
      return !s.pts.some((x) => x.id === p.playtestId);
    case 'member.remove':
      return !s.users.some((u) => u.k === p.userKey);
    case 'member.upsert': {
      const u = s.users.find((x) => x.k === p.user.k);
      return !!u && u.name === p.user.name && u.role === p.user.role;
    }
    // Keyed by (appid, key), not by id: the optimistic row is still wearing its
    // `pending_` placeholder when its own INSERT echoes back.
    case 'video.upsert':
      return s.videos.some((v) => v.appid === p.video.appid && v.key === p.video.key);
    case 'video.remove':
      return !s.videos.some((v) => v.id === p.videoId);
    default:
      return true;
  }
}

/**
 * §2.4's toast table. `name` is the actor's display name.
 *
 * P6 F-4: three kinds have **no actor**. `pt_window_open` / `pt_window_close`
 * are emitted by `public.tg_event_pt_window()` (migrations 1200 + 1801) and
 * `pt_wave` by `playtest-scan`'s CCU arm — all three insert `actor_id: null`,
 * because nobody in the crew did them. They are exactly the events INVENTORY §9
 * promises out loud (`OPENS/CLOSES + DETECTED WAVES POST TO WHAT'S NEW`), so
 * `handleCrewEvent` lets an empty `actorKey` through and `SYSTEM_EVENT_KINDS`
 * keeps them out of the per-actor "MADE N CALLS" collapse.
 *
 * Note the spelling: `pt_window_close`, **not** `…closed` (P6-02). That is the
 * kind in migration 1200, in ARCHITECTURE §1.8's list, and in the rows already
 * in the table.
 */
export const SYSTEM_EVENT_KINDS: ReadonlySet<string> = new Set([
  'pt_window_open',
  'pt_window_close',
  'pt_wave',
  // `round_sweep_idle()` (3039) closes a round nobody has touched in three
  // hours; no member did it, so the toast says the clock did.
  'round_idle',
]);

export function eventToast(e: CrewEvent, actorName: string): string {
  const who = actorName.toUpperCase();
  const what = String(e.payload['name'] ?? '').toUpperCase();
  switch (e.kind) {
    case 'list_add':
      return what ? who + ' ADDED ' + what : '';
    case 'vote': {
      // Both alphabets, permanently. `crew_events` is history and the 4-tier
      // migration deliberately does not rewrite it (docs/porting/V.md decision
      // 4), so the tail of events the snapshot carries holds `y`/`n` forever —
      // and an un-reloaded old tab keeps writing them. Same map as
      // `sanitizeVotes`, and an unreadable value toasts nothing rather than
      // guessing a call: the row still bumps the ◆ badge.
      const raw = String(e.payload['value'] ?? '');
      const l = raw === 'y' ? 'a' : raw === 'n' ? 'c' : raw;
      return what && isCall(l) ? who + ' CALLED ' + LVL[l].g + ' ON ' + what : '';
    }
    case 'comment':
      return what ? who + ' COMMENTED ON ' + what : '';
    case 'video_add':
      // ▶ marks the one kind whose subject is a piece of media rather than a
      // call, so it reads as different at a glance in a stack of toasts.
      return what ? '▶ ' + who + ' ADDED A VIDEO — ' + what : '';
    case 'pt_track':
      return what ? who + ' IS TRACKING ' + what : '';
    case 'pt_granted':
      return what ? who + ' GOT INTO ' + what : '';
    case 'member_add':
      return String(e.payload['display_name'] ?? '').toUpperCase() + ' JOINED THE PARTY';
    case 'member_remove':
      return String(e.payload['display_name'] ?? '').toUpperCase() + ' LEFT THE PARTY';
    // -- rounds (3039) --------------------------------------------------------
    // `round_start` deliberately toasts NOTHING: the one-shot join card is the
    // notification for a round starting (changelog 2026-08-25), and a toast on
    // top of it would say the same thing twice.
    case 'round_start':
      return '';
    case 'round_lock':
      return what ? who + ' LOCKED IT IN — TONIGHT: ' + what : who + ' LOCKED IN THE PICK';
    case 'round_end':
      return what ? who + ' ENDED GAME NIGHT · TONIGHT: ' + what : who + ' ENDED GAME NIGHT';
    // The kicked player's own screen already shows them out; this is how the
    // REST of the crew learns a seat went, and why the roster shrank.
    case 'round_kick': {
      const gone = String(e.payload['display_name'] ?? '').toUpperCase();
      return gone ? who + ' TOOK ' + gone + ' OUT OF GAME NIGHT' : '';
    }
    case 'round_idle':
      return "GAME NIGHT CLOSED — NOBODY'S TOUCHED IT IN 3 HOURS";
    // -- system events (no actor) --------------------------------------------
    case 'pt_window_open':
      // The scraper's signal is `status → 'open'`: the request-access widget is
      // on the store page. `SIGNUPS OPEN` is the row's own `foot` (INVENTORY §9).
      return what ? what + ' — SIGNUPS OPEN' : '';
    case 'pt_window_close':
      // Two ways to get here: a dated window elapsed (playtest-scan inserts it
      // with `reason: 'window_end elapsed'`, P6-03) or the widget went away
      // after three confirmed-missing scans → `NO SIGNUP PAGE`.
      if (!what) return '';
      return e.payload['reason'] === 'window_end elapsed'
        ? what + ' — TEST WINDOW CLOSED'
        : what + ' — SIGNUPS CLOSED';
    case 'pt_wave': {
      // Copy from the function's own payload, INVENTORY §9 seeded style:
      // `WAVE DETECTED · <N> IN THE PLAYTEST APP`.
      if (!what) return '';
      const ccu = Number(e.payload['ccu']);
      return Number.isFinite(ccu) && ccu > 0
        ? what + ' — WAVE DETECTED · ' + ccu + ' IN THE PLAYTEST APP'
        : what + ' — WAVE DETECTED';
    }
    default:
      return '';
  }
}
